import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FieldType } from '@ngx-formly/core';
import {
  FormViewService,
  IntegrationMappingService,
  SubmissionService,
  ToastService
} from '@xilo-mono/form-contracts';
import { GooglePlaceDirective } from 'ngx-google-places-autocomplete';
import { Address } from 'ngx-google-places-autocomplete/objects/address';
import { ReplaySubject, takeUntil } from 'rxjs';
export interface InitialAddress {
  streetAddress2:string,
  city: string;
  state: string;
  zipCode: any;
  canEdit: boolean;
}
  
@Component({
  selector: 'xilo-mono-form-viewer-addressv2',
  templateUrl: './address-v2.component.html',
  styleUrls: ['./../address/address.component.scss', '../../form-viewer.component.scss'],
})

export class AddressV2Component
  extends FieldType
  implements AfterViewInit, OnDestroy, OnInit {
  @ViewChild('placesRef') placesRef: GooglePlaceDirective;
  @ViewChild('addressInput') addressInputRef: ElementRef;

  googlePlacesOptions = {
    bounds: null,
    types: [],
    componentRestrictions: {
      country: 'US',
    },
    fields: [],
    strictBounds: false,
    origin: null,
  };
  errorMessage = '';
  hasError = false;
  addressSelected = false;
  streetAddress;
  unitNumber;
  city;
  state;
  zipCode;
  initialAddress: InitialAddress;
  formatted_address:string;

  constructor(
    public formViewService: FormViewService,
    private integrationMappingService: IntegrationMappingService,
    private submissionService: SubmissionService,
    private toastService: ToastService
  ) {
    super();
  }

  private readonly destroyed$ = new ReplaySubject<boolean>(1);

  ngOnInit(): void {
    this.initializeAddressV2();
  }

  ngAfterViewInit(): void {
    this.setPlacesOptions();
    this.formControl.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        this.formViewService.updateForm.next(true);
      });
    if (this.addressInputRef && this.addressInputRef.nativeElement) {
      this.addressInputRef.nativeElement.addEventListener('keydown', (event: KeyboardEvent) => {
        if (event.key === 'Enter' && (event.shiftKey || event.metaKey || event.ctrlKey)) {
          event.preventDefault();
          event.stopPropagation();
          const customEvent = new CustomEvent('addressNavigationKeyPressed', {
            bubbles: true,
            detail: { key: event.key, modifiers: { shift: event.shiftKey, meta: event.metaKey, ctrl: event.ctrlKey } }
          });
          this.addressInputRef.nativeElement.dispatchEvent(customEvent);
          this.addressInputRef.nativeElement.blur();
        }
      });
    }
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  touched() {
    this.errorMessage = null
    this.formControl.markAllAsTouched();
    if ((this.formControl.value === '' || this.formControl.value === null) && this.formControl.hasError('required'))
      this.errorMessage = "This field is required";
    else if (this.formControl.value && !this.addressSelected && this.formControl.touched) {
      this.hasError = true;
      this.formControl.setErrors({ addressSelection: true });
      this.errorMessage = 'You must select an address from the dropdown';
    }
  }

  // handleAddressChange : get address on input change from zillow API
  handleAddressChange(address: Address) {
    const addressFieldKey = this.field.templateOptions.dataKey;

    const indexArray = address['address_components'].map((item) => {
      return item.types[0];
    });

    const streetNumber = address['address_components'][
      indexArray.indexOf('street_number')
    ]
      ? address['address_components'][indexArray.indexOf('street_number')]
          .long_name
      : null;

    const streetName = address['address_components'][
      indexArray.indexOf('route')
    ]
      ? address['address_components'][indexArray.indexOf('route')].long_name
      : null;

    this.unitNumber = address['address_components'][
      indexArray.indexOf('subpremise')
    ]
      ? address['address_components'][indexArray.indexOf('subpremise')]
          .long_name
      : null;


    this.streetAddress =
      streetNumber && streetName ? `${streetNumber} ${streetName}` : null;

    // Need to account for neighborhoods like Jamaica, Queens
    let cityCode = indexArray.indexOf('locality');
    if (cityCode < 0) {
      cityCode = indexArray.indexOf('sublocality_level_1');
    }
    this.city = address['address_components'][cityCode]
      ? address['address_components'][cityCode].long_name
      : null;

    const county = address['address_components'][
      indexArray.indexOf('administrative_area_level_2')
    ]
      ? address['address_components'][
          indexArray.indexOf('administrative_area_level_2')
        ].long_name
      : null;

    this.state = address['address_components'][
      indexArray.indexOf('administrative_area_level_1')
    ]
      ? address['address_components'][
          indexArray.indexOf('administrative_area_level_1')
        ].short_name
      : null;

    this.zipCode = address['address_components'][
      indexArray.indexOf('postal_code')
    ]
      ? address['address_components'][indexArray.indexOf('postal_code')]
          .long_name
      : null;

    const fullAddress = address['formatted_address'];
    this.initialAddress = {
      city: this.city,
      state: this.state,
      zipCode: this.zipCode,
      streetAddress2: this.unitNumber,
      canEdit: false
    }
    if (streetNumber && streetName && this.city && this.state && this.zipCode) {
      if (fullAddress) {
        this.formControl.patchValue(fullAddress);
      }

      const streetNumberField = this.field.parent.fieldGroup.find((field) => {
        return (
          field.templateOptions.dataKey === addressFieldKey + 'streetNumber'
        );
      });

      if (streetNumberField) {
        streetNumberField.formControl.patchValue(streetNumber);
      }

      const streetNameField = this.field.parent.fieldGroup.find((field) => {
        return field.templateOptions.dataKey === addressFieldKey + 'streetName';
      });

      if (streetNameField) {
        streetNameField.formControl.patchValue(streetName);
      }

      const streetAddressField = this.field.parent.fieldGroup.find((field) => {
        return (
          field.templateOptions.dataKey === addressFieldKey + 'streetAddress'
        );
      });

      if (streetAddressField) {
        streetAddressField.formControl.patchValue(this.streetAddress);
      }

      const streetAddress2 = this.field.parent.fieldGroup.find((field) => {
        return field.templateOptions.dataKey === addressFieldKey + 'streetAddress2';
      });

      if (streetAddress2) {
        streetAddress2.formControl.patchValue(this.unitNumber);
      }

      const cityField = this.field.parent.fieldGroup.find((field) => {
        return field.templateOptions.dataKey === addressFieldKey + 'city';
      });

      if (cityField) {
        cityField.formControl.patchValue(this.city);
      }

      const stateField = this.field.parent.fieldGroup.find((field) => {
        return field.templateOptions.dataKey === addressFieldKey + 'state';
      });

      if (stateField) {
        stateField.formControl.patchValue(this.state);
      }

      const zipCodeField = this.field.parent.fieldGroup.find((field) => {
        return field.templateOptions.dataKey === addressFieldKey + 'zipCode';
      });

      if(zipCodeField){
        zipCodeField.formControl.patchValue(this.zipCode);
      }

      const countyField = this.field.parent.fieldGroup.find((field) => {
        return field.templateOptions.dataKey === addressFieldKey + 'county';
      });

      if (countyField) {
        countyField.formControl.patchValue(county);
      }

      this.submissionService.onUpdateMetadata(
        {
          key: 'fullAddress',
          value: fullAddress,
        },
        this.field
      );

      if (this.to.className !== 'xilo-input') {
        this.formViewService.updateForm.next(true);
      }
      this.formViewService.addressV2.next(this.initialAddress);
      this.integrationMappingService.initDataPull(
        this.to?.pullDataTrigger,
        this.to?.dataPullMappingId,
        this.field?.parent?.parent?.templateOptions?.subIndex
      );
      this.hasError = false;
      this.errorMessage = null;
      setTimeout(() => {
        this.addressSelected = true;
        this.formControl.setErrors(null);
        this.formControl.markAllAsTouched();
        this.formControl.updateValueAndValidity();
      });
      setTimeout(() => {
        this.addressSelected = false;
      }, 1000);
    } else {
      this.hasError = true;
      this.addressSelected = false;
      this.errorMessage = 'Invalid address was selected. Please check address';
    }
  }

  openZillow(address) {
    const fullAddress = address.replace(/(?: USA)/g, '');
    this.formViewService.listZillowData({ fullAddress }).pipe(
      takeUntil(this.destroyed$)
    ).subscribe(
      (data) => {
        window.open(data, '_blank');
      },
      (error) => {
        this.toastService.showWarn(error.error.error.message);
        console.log(error, false);
      }
    );
  }

  // editAddress
  editAddress(){
    this.formViewService.addressV2.next({
      ...this.initialAddress,
      canEdit: true
    })
  }

  // initializeAddressV2 : pulls and populates address data on input init
  private initializeAddressV2() {
    const addressFieldKey = this.field.templateOptions.dataKey;
    const cityField = this.field.parent.fieldGroup.find((field) => {
      return field.templateOptions.dataKey === addressFieldKey + 'city';
    });
    const stateField = this.field.parent.fieldGroup.find((field) => {
      return field.templateOptions.dataKey === addressFieldKey + 'state';
    });
    const zipCodeField = this.field.parent.fieldGroup.find((field) => {
      return field.templateOptions.dataKey === addressFieldKey + 'zipCode';
    }); 
    const streetAddress2 = this.field.parent.fieldGroup.find((field) => {
      return field.templateOptions.dataKey === addressFieldKey + 'streetAddress2';
    });
    this.formViewService.addressV2.next({
      streetAddress2: streetAddress2.formControl.value,
      city: cityField.formControl.value,
      state: stateField.formControl.value,
      zipCode: zipCodeField.formControl.value,
      canEdit: false
    });
  }

  private setPlacesOptions() {
    // Note: [options] for ngx-google-places-autocomplete conflicts with formly, so this must be done manually
    this.placesRef.options = this.googlePlacesOptions;
    this.placesRef.reset();
  }
}
